import React, {ReactNode, useCallback} from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'

const ARROW_SIZE = 8
const ARROW_OFFSET = 4
const TOOLTIP_GAP = 24

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  padding-bottom: ${cssTheme.layout.spacing.s500};
`

const StyledTooltip = styled.div`
  position: absolute;
  left: calc(100% + ${TOOLTIP_GAP}px);
  top: 50%;
  transform: translateY(-50%);
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  background-color: ${cssTheme.colors.tooltip.background};
  width: 340px;
  z-index: 4;
`

const StyledArrow = styled.div`
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  position: absolute;
  left: -${ARROW_OFFSET}px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  background-color: ${cssTheme.colors.tooltip.background};
  border-radius: 0 ${cssTheme.layout.radius.xs};
`

const CloseButtonWrapper = styled.div`
  color: ${cssTheme.colors.tooltip.content};
  position: absolute;
  right: ${cssTheme.layout.spacing.s200};
  top: ${cssTheme.layout.spacing.s300};
`

interface PriceInsightsTooltipProps {
  isOpen: boolean
  onClose: () => void
  content: ReactNode
  children: ReactNode
}

export const PriceInsightsTooltip = ({
  isOpen,
  onClose,
  content,
  children
}: PriceInsightsTooltipProps) => {
  const handleCloseClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      onClose()
    },
    [onClose]
  )

  return (
    <Wrapper>
      {children}
      {isOpen && (
        <StyledTooltip>
          <Box
            display="flex"
            alignItems="flex-start"
            paddingTop="s500"
            paddingBottom="s500"
            paddingRight="s800"
            paddingLeft="s600"
            gap="s000"
          >
            <div>
              <Text variant="bodyS" colorPath="tooltip.content">
                {content}
              </Text>
            </div>
            <CloseButtonWrapper>
              <Button
                hasTouchArea
                variant="ghostInverse"
                size="md"
                iconStart={
                  <Icon name="Close" size="md" colorPath="tooltip.content" />
                }
                onClick={handleCloseClick}
                aria-label="Close tooltip"
              />
            </CloseButtonWrapper>
          </Box>
          <StyledArrow />
        </StyledTooltip>
      )}
    </Wrapper>
  )
}
